import { useCallback, useEffect, useState } from "react";
import { Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, shiftName, SHIFT_STYLE, STATUS_STYLE, todayWIB } from "@/components/staff/attendance/utils";

const input = "px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm w-full";

const RequestForm = ({ config, coworkers, onSent }) => {
  const [f, setF] = useState({ date: todayWIB(), to_shift: "", partner_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/attendance/shift-requests", f); toast.success("Pengajuan terkirim, menunggu persetujuan admin"); setF((x) => ({ ...x, to_shift: "", partner_id: "", reason: "" })); onSent(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="shift-request-form">
      <label className="text-xs">Tanggal<input type="date" required min={todayWIB()} value={f.date} onChange={set("date")} className={input} data-testid="shift-request-date-input" /></label>
      <label className="text-xs">Shift tujuan
        <select required value={f.to_shift} onChange={set("to_shift")} className={input} data-testid="shift-request-to-select">
          <option value="">Pilih shift</option>
          {config.shifts.map((s) => <option key={s.id} value={s.id}>{s.label} ({s.start}–{s.end})</option>)}
          <option value="libur">Libur</option>
        </select>
      </label>
      <label className="text-xs">Tukar dengan rekan (opsional)
        <select value={f.partner_id} onChange={set("partner_id")} className={input} data-testid="shift-request-partner-select">
          <option value="">Tanpa rekan</option>
          {coworkers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.division ? ` · ${c.division}` : ""}</option>)}
        </select>
      </label>
      <label className="text-xs">Alasan<input value={f.reason} onChange={set("reason")} maxLength={200} placeholder="mis. ada keperluan keluarga" className={input} data-testid="shift-request-reason-input" /></label>
      <button disabled={busy} className="btn-forest px-4 py-2 text-sm sm:col-span-2 justify-self-end" data-testid="shift-request-submit-button">{busy ? "Mengirim..." : "Ajukan tukar shift"}</button>
    </form>
  );
};

export const MyShiftSchedule = ({ config, me }) => {
  const [week, setWeek] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [coworkers, setCoworkers] = useState([]);
  const load = useCallback(() => {
    api.get("/attendance/my-schedule").then((r) => setWeek(r.data)).catch(() => {});
    api.get("/attendance/shift-requests/mine").then((r) => setReqs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/attendance/coworkers").then((r) => setCoworkers(r.data)).catch(() => {}); }, [load]);
  const cancel = async (id) => {
    try { await api.delete(`/attendance/shift-requests/${id}`); toast.success("Pengajuan dibatalkan"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-paper p-5 mt-5" data-testid="my-shift-schedule">
      <h3 className="font-semibold flex items-center gap-2"><Repeat className="w-4 h-4" /> Jadwal saya minggu ini</h3>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mt-3">
        {week?.dates.map((d) => {
          const v = week.days[d];
          return (
            <div key={d} className={`rounded-lg border px-2 py-2 text-center text-xs ${SHIFT_STYLE[v.shift] || SHIFT_STYLE[""]} ${d === todayWIB() ? "ring-2 ring-forest" : ""}`} data-testid={`my-schedule-${d}`}>
              <div className="opacity-80">{fmtDate(d)}</div>
              <div className="font-semibold mt-0.5">{shiftName(config, v.shift)}</div>
              {v.source === "tukar" && <div className="text-[10px]">tukar</div>}
            </div>
          );
        })}
      </div>
      <h4 className="font-semibold mt-5">Ajukan tukar shift</h4>
      <p className="text-xs text-muted2">Jadwal baru berlaku setelah disetujui admin. Jika memilih rekan, jadwal kalian ditukar.</p>
      <RequestForm config={config} coworkers={coworkers} onSent={load} />
      {reqs.length > 0 && (
        <div className="divide-y divide-sage-200 mt-4 text-sm" data-testid="my-shift-requests">
          {reqs.map((r) => (
            <div key={r.id} className="py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid={`my-shift-request-${r.id}`}>
              <div>
                <div>{fmtDate(r.date)} · {r.user_id === me.id ? <>{shiftName(config, r.from_shift)} → <b>{shiftName(config, r.to_shift)}</b>{r.partner_name && ` · dengan ${r.partner_name}`}</> : <>Diajak tukar oleh {r.name}: kamu {shiftName(config, r.partner_from_shift)} → <b>{shiftName(config, r.from_shift)}</b></>}</div>
                {r.admin_note && <div className="text-xs text-muted2">Catatan admin: {r.admin_note}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                {r.status === "menunggu" && r.user_id === me.id && <button onClick={() => cancel(r.id)} className="text-xs text-red-700 font-semibold" data-testid={`my-shift-request-cancel-${r.id}`}>Batalkan</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
